import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import { ElMessage } from 'element-plus'

import { getConfig } from '@/common/config'
import * as oauth2Helper from '@/common/oauth2/oauth2Helper'
import { AuthModeEnum, SIGNIN_PATH } from '@/constants'
import { i18n } from '@/langs'
import { useOAuth2Store } from '@/stores/oauth2Store'

import { AxiosCanceler } from './axiosCancel'
import type { CustomAxiosRequestConfig } from '../interface'

const config = {
  baseURL: '',
  timeout: 60000
}

const axiosCanceler = new AxiosCanceler()

class RequestHttp {
  service: AxiosInstance

  public constructor(config: AxiosRequestConfig) {
    this.service = axios.create(config)

    this.service.interceptors.request.use(
      async (config: CustomAxiosRequestConfig) => {
        const configData = getConfig()
        if (!configData) {
          return Promise.reject(new Error(i18n.global.t('api.configuration_not_available')))
        }

        config.baseURL = configData.apiBaseUrl
        const authMode = configData.authMode
        const oauth2Store = useOAuth2Store()

        config.cancel ??= true
        config.cancel && axiosCanceler.addPending(config)

        if (!authMode || authMode === 'none' || authMode === 'None') {
          return Promise.resolve(config)
        }

        if (authMode === AuthModeEnum.OAUTH2 || authMode === AuthModeEnum.LOCAL) {
          const tokenInfo = oauth2Store.getterTokenInfo

          try {
            const at = await oauth2Helper.getAccessToken(tokenInfo)
            if (at) {
              config.headers.Authorization = 'Bearer ' + at
            }

            return Promise.resolve(config)
          } catch (error) {
            console.error('error in get access token in axios.', error)
            return Promise.reject(error)
          }
        }

        return Promise.resolve(config)
      },
      (error: AxiosError) => {
        return Promise.reject(error)
      }
    )

    this.service.interceptors.response.use(
      (response: AxiosResponse & { config: CustomAxiosRequestConfig }) => {
        const { data, config } = response

        axiosCanceler.removePending(config)

        if (config.responseType === 'blob') {
          return response
        }

        return data
      },
      async (error: AxiosError) => {
        const { response, config: errorConfig } = error
        if (errorConfig) {
          axiosCanceler.removePending(errorConfig as CustomAxiosRequestConfig)
        }

        if (response?.status === 401 && errorConfig && !(errorConfig as any)._retried) {
          const oauth2Store = useOAuth2Store()
          const rt = oauth2Store.getterRefreshToken

          if (rt) {
            try {
              (errorConfig as any)._retried = true
              const ti = await oauth2Store.doRefreshToken()
              errorConfig.headers = errorConfig.headers || {}
              errorConfig.headers.Authorization = 'Bearer ' + ti.accessToken
              return this.service.request(errorConfig)
            } catch {
              oauth2Store.signout()
              window.location.hash = '#' + SIGNIN_PATH
              return Promise.reject(error)
            }
          }

          oauth2Store.signout()
          window.location.hash = '#' + SIGNIN_PATH
          return Promise.reject(error)
        }

        if (!response) {
          if (error.message.indexOf('timeout') !== -1) {
            ElMessage.error(i18n.global.t('api.request_timeout'))
          } else if (error.message.indexOf('Network Error') !== -1) {
            ElMessage.error(i18n.global.t('api.network_error'))
          }
        }

        return Promise.reject(error)
      }
    )
  }

  get<T>(url: string, params?: object, _object = {}): Promise<T> {
    return this.service.get(url, { params, ..._object })
  }
  post<T>(url: string, params?: object | string, _object = {}): Promise<T> {
    return this.service.post(url, params, _object)
  }
  put<T>(url: string, params?: object, _object = {}): Promise<T> {
    return this.service.put(url, params, _object)
  }
  delete<T>(url: string, params?: object, _object = {}): Promise<T> {
    return this.service.delete(url, { params, ..._object })
  }
  download(url: string, params?: object, _object = {}): Promise<{ data: BlobPart }> {
    return this.service.post(url, params, { ..._object, responseType: 'blob' })
  }
}

const http = new RequestHttp(config)

export default http
